<template>
  <PreAuthPage>
    <div class="sh-card__head" data-testid="invitation-page">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <h1 class="sh-card__title">{{ title }}</h1>
      <p class="sh-card__subtitle">{{ subtitle }}</p>
    </div>

    <!-- 🔴 The server's own sentence, unchanged. One message for every way a token can be no
         good — unknown, spent, ended — so passing it through leaks nothing. -->
    <p v-if="errorMessage" class="invitation__error sh-card__error" role="alert" data-testid="invitation-error">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ errorMessage }}</span>
    </p>

    <!-- A brand-new invitee: choose a password on the link, and that is the acceptance. -->
    <form v-if="mode === 'claim'" class="login-form" data-testid="invitation-claim-form" @submit.prevent="claim()">
      <label class="login-field">
        <span class="login-field__label">{{ t("invitation.newPassword") }}</span>
        <input
          ref="passwordInput"
          v-model="newPassword"
          class="login-field__input"
          type="password"
          name="new-password"
          autocomplete="new-password"
          required
          data-testid="invitation-password"
          :disabled="busy"
        />
      </label>
      <label class="login-field">
        <span class="login-field__label">{{ t("invitation.confirmPassword") }}</span>
        <input
          v-model="confirmPassword"
          class="login-field__input"
          type="password"
          name="confirm-password"
          autocomplete="new-password"
          required
          data-testid="invitation-confirm"
          :disabled="busy"
        />
      </label>
      <v-btn type="submit" color="primary" size="large" block :loading="busy" data-testid="invitation-claim">{{ t("invitation.claim") }}</v-btn>
    </form>

    <!-- Somebody who already has a way in signs in AS THEMSELVES and comes back here to accept. -->
    <v-btn v-else-if="mode === 'sign-in'" color="primary" variant="flat" size="large" block data-testid="invitation-sign-in" @click="toSignIn()">{{
      t("invitation.signIn")
    }}</v-btn>

    <!-- Claimed: nothing is signed in by claiming — the new password is proved at the front door
         like anybody else's, which keeps ONE way into this application. -->
    <v-btn v-else-if="mode === 'claimed'" color="primary" variant="flat" size="large" block data-testid="invitation-to-sign-in" @click="toHome()">{{
      t("invitation.toSignIn")
    }}</v-btn>

    <v-btn v-else-if="mode === 'accepted'" color="primary" variant="flat" size="large" block data-testid="invitation-carry-on" @click="toHome()">{{
      t("invitation.carryOn")
    }}</v-btn>

    <!-- D161: an onboarding link with a session already — nothing to accept; the details are on their profile. -->
    <v-btn v-else-if="mode === 'onboarding'" color="primary" variant="flat" size="large" block data-testid="invitation-open-onboarding" @click="toOnboarding()">{{
      t("invitation.onboardingOpen")
    }}</v-btn>
  </PreAuthPage>
</template>

<script lang="ts">
/**
 * 🔴 **THE FAR END OF AN INVITATION LINK** (D126) — `/i/<token>`.
 *
 * Drawn by `App.vue` straight off the path, beside `/r/<token>`. Three people can arrive here and
 * the page is decided by which one, read from the server and the session rather than guessed:
 *
 *   · **Nobody yet** (`hasCredential: false`, no session) — they choose a password, and that one act
 *     accepts the offer and opens the account (`POST /invitations/claim`).
 *   · **Somebody with an account, signed out** — they are sent to sign in as themselves, and the
 *     shell brings them back here (`rememberReturnTo`) to accept.
 *   · **Somebody signed in** — the acceptance is made now, behind their session (`POST
 *     /memberships/accept`): the token proves the LINK, the session proves the PERSON (D22).
 *
 * 🔴 **THE TOKEN IS NEVER PUT BACK INTO A LINK OR A LOG.** It is read once from the path into a prop;
 * once spent, the address bar is rewritten to `/` with `replaceState` so it is not left in history
 * on a shared machine.
 */
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { acceptInvitation, claimInvitation, readInvitation, type OpenInvitation } from "@/auth/invitation";
import { rememberReturnTo } from "@/auth/sessionGone";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import { APP_BRANDING, APP_NAME } from "@/app";

type Mode = "loading" | "invalid" | "claim" | "sign-in" | "accepting" | "claimed" | "accepted" | "onboarding";

/** Where an engagement onboarding lives once the person is in (D161): the profile module's own section. */
const ONBOARDING_RETURN = { pathname: "/profile", search: "?section=onboarding" } as const;

export default defineComponent({
  name: "Invitation",
  components: { LogoTile, PreAuthPage },
  props: {
    /** The token out of `/i/<token>`. Read by App.vue so this view never parses a URL. */
    token: { type: String, required: true },
    /** The session's status, from the store — passed in so this view is drawn the same above and below a session. */
    sessionStatus: { type: String, required: true },
  },
  setup(props) {
    const { t } = useI18n();
    const tenantName = APP_NAME;
    const branding = APP_BRANDING;

    const invitation = ref<OpenInvitation | null>(null);
    const mode = ref<Mode>("loading");
    const errorMessage = ref<string | null>(null);
    const newPassword = ref("");
    const confirmPassword = ref("");
    const busy = ref(false);
    const passwordInput = ref<HTMLInputElement | null>(null);

    const org = computed(() => invitation.value?.organisationName ?? "");
    const email = computed(() => invitation.value?.email ?? "");
    /** D159e/D161: a production's onboarding link, on this same page. It names no company and no engagement. */
    const onboarding = computed(() => invitation.value?.kind === "onboarding");

    const title = computed(() => {
      if (mode.value === "claimed") return t("invitation.claimed");
      if (onboarding.value && mode.value !== "invalid") return t("invitation.onboardingTitle");
      if (mode.value === "accepted") return t("invitation.accepted", { org: org.value });
      if (mode.value === "invalid") return t("invitation.titleBare");
      return org.value === "" ? t("invitation.titleBare") : t("invitation.title", { org: org.value });
    });
    const subtitle = computed(() => {
      switch (mode.value) {
        case "loading":
          return t("invitation.loading");
        case "invalid":
          return t("invitation.invalid");
        case "claim":
          return onboarding.value ? t("invitation.onboardingChooseIntro", { email: email.value }) : t("invitation.chooseIntro", { email: email.value });
        case "sign-in":
          return onboarding.value ? t("invitation.onboardingSignInIntro", { email: email.value }) : t("invitation.signInIntro", { email: email.value });
        case "onboarding":
          return t("invitation.onboardingReady");
        case "accepting":
          return t("invitation.accepting");
        case "claimed":
          return onboarding.value ? t("invitation.onboardingClaimedHint") : t("invitation.claimedHint", { org: org.value });
        case "accepted":
          return t("invitation.acceptedHint");
        default:
          return "";
      }
    });

    /** The spent token leaves the address bar — see the header. */
    const forgetToken = (): void => window.history.replaceState(null, "", "/");

    async function accept(): Promise<void> {
      mode.value = "accepting";
      const outcome = await acceptInvitation(props.token);
      if (!outcome.ok) {
        errorMessage.value = outcome.message;
        mode.value = "invalid";
        return;
      }
      forgetToken();
      mode.value = "accepted";
    }

    /** Which page this is, decided from what the server said and whether there is a session. */
    function decide(): void {
      const open = invitation.value;
      if (open === null || mode.value === "claimed" || mode.value === "accepted" || mode.value === "accepting") return;
      if (props.sessionStatus === "ready") {
        // An onboarding link has nothing to accept behind a session: the person's details are on their profile.
        if (open.kind === "onboarding") {
          mode.value = "onboarding";
          return;
        }
        void accept();
        return;
      }
      if (props.sessionStatus === "resolving" || props.sessionStatus === "signing-in") {
        mode.value = "loading";
        return;
      }
      mode.value = open.hasCredential ? "sign-in" : "claim";
    }

    onMounted(async () => {
      const outcome = await readInvitation(props.token);
      if (!outcome.ok) {
        errorMessage.value = outcome.message;
        mode.value = "invalid";
        return;
      }
      invitation.value = outcome.invitation;
      decide();
    });

    // A session that resolves after the page is up — or arrives from the sign-in screen — is what
    // turns "sign in to accept" into the acceptance itself.
    watch(
      () => props.sessionStatus,
      () => decide(),
    );
    watch(mode, (now) => {
      if (now === "claim") queueMicrotask(() => passwordInput.value?.focus?.());
    });

    async function claim(): Promise<void> {
      if (busy.value) return;
      errorMessage.value = null;
      // 🔴 Checked HERE and not at the server: a mismatch is a typing question between the person
      // and their own screen, and sending a password twice only widens what is on the wire.
      if (newPassword.value !== confirmPassword.value) {
        errorMessage.value = t("invitation.mismatch");
        return;
      }
      busy.value = true;
      try {
        const outcome = await claimInvitation(props.token, newPassword.value);
        if (!outcome.ok) {
          errorMessage.value = outcome.message;
          return;
        }
        newPassword.value = "";
        confirmPassword.value = "";
        forgetToken();
        // After the front-door sign-in, land on the details the production asked for — not the home page.
        if (onboarding.value) rememberReturnTo(ONBOARDING_RETURN.pathname, ONBOARDING_RETURN.search);
        mode.value = "claimed";
      } finally {
        busy.value = false;
      }
    }

    /** Remember this page, then a full load at "/" — the shell draws its own sign-in screen there
     *  and, once the person is in, brings them back to this link to accept. */
    function toSignIn(): void {
      if (onboarding.value) rememberReturnTo(ONBOARDING_RETURN.pathname, ONBOARDING_RETURN.search);
      else rememberReturnTo(`/i/${props.token}`, "");
      window.location.assign("/");
    }

    function toOnboarding(): void {
      forgetToken();
      window.location.assign(`${ONBOARDING_RETURN.pathname}${ONBOARDING_RETURN.search}`);
    }

    function toHome(): void {
      window.location.assign("/");
    }

    return { t, tenantName, branding, mode, title, subtitle, errorMessage, newPassword, confirmPassword, busy, passwordInput, claim, toSignIn, toHome, toOnboarding };
  },
});
</script>

<style scoped>
/* `login-form` / `login-field` rather than classes of our own, for the reason ResetPassword.vue
   gives: `standalone.css` styles the control as `.login-form .login-field__input`. */
.invitation__error {
  margin-bottom: 12px;
}
</style>
